/**
 * Internationalization (i18n) module for WhatToEat.
 *
 * Usage:
 *   import { i18n, setLanguage } from './i18n';
 *
 *   // Access strings directly:
 *   console.log(i18n.appName);  // "WhatToEat" or "今天7什么"
 *
 *   // Change language:
 *   setLanguage('zh');
 *
 * Adding a new string:
 *   Just add one line to STRINGS:
 *   myNewString: { en: 'English text', zh: '中文文本' },
 */

export type Language = 'en' | 'zh';

/**
 * All UI strings with both English and Chinese translations co-located.
 * Adding a new string only requires adding ONE line here.
 */
const STRINGS = {
    // App header
    appName: { en: 'WhatToEat', zh: '今天7什么' },
    subtitle: { en: 'Your AI-powered dining companion', zh: '你的AI美食助手' },
    newChat: { en: 'New Chat', zh: '新对话' },

    // Welcome message
    welcomeTitle: { en: 'Welcome!', zh: '欢迎！' },
    welcomeText: { en: 'Ask me for restaurant recommendations, what to eat, or anything food-related.', zh: '问我餐厅推荐、吃什么，或任何与美食相关的问题。' },
    welcomeExample: { en: 'Try: "What should I eat for dinner tonight?"', zh: '试试："今晚吃什么好？"' },
    welcomeExample2: { en: '"How to use this app?"', zh: '"如何使用这个应用？"' },
    welcomeExample3: { en: '"Search for good ramen places near me"', zh: '"帮我搜索附近好吃的餐厅并添加到数据库"' },
    placeholder: { en: 'Ask for restaurant recommendations...', zh: '问我吃什么...' },
    photoHintInline: { en: 'Photos shown', zh: '已显示照片' },
    photoHintTooltip: { en: 'Show photos tag for', zh: '显示照片标记：' },

    // Onboarding (for new users with few restaurants)
    onboardingTitle: { en: 'Getting Started', zh: '快速入门' },
    onboardingStep1: { en: '1. Add restaurants you like to your database (Restaurants tab → + Add Restaurant)', zh: '1. 添加你喜欢的餐厅到数据库（餐厅标签页 → + 添加餐厅）' },
    onboardingStep2: { en: '2. Set your location filter at the top of the chat', zh: '2. 在聊天顶部设置位置筛选' },
    onboardingStep3: { en: '3. Ask me for recommendations!', zh: '3. 向我请求推荐！' },

    // History panel
    historyTitle: { en: 'Past Conversations', zh: '历史对话' },
    historyEmpty: { en: 'No past conversations yet.', zh: '暂无历史对话。' },
    historyNotice: { en: 'Viewing past conversation from', zh: '查看历史对话，创建于' },
    historyHint: { en: 'Send a message to continue this conversation or click "New Chat" to start fresh.', zh: '发送消息继续对话，或点击"新对话"开始新的聊天。' },

    // Navigation
    navChat: { en: 'Chat', zh: '聊天' },
    navRestaurants: { en: 'Restaurants', zh: '餐厅' },

    // Restaurant view
    restaurantsTitle: { en: 'Restaurant Database', zh: '餐厅数据库' },
    addRestaurant: { en: '+ Add Restaurant', zh: '+ 添加餐厅' },
    restaurantsEmpty: { en: 'No restaurants in database yet. Add your first one!', zh: '数据库中暂无餐厅，添加第一家吧！' },
    formTitle: { en: 'Add New Restaurant', zh: '添加新餐厅' },
    cancel: { en: 'Cancel', zh: '取消' },
    submit: { en: 'Add Restaurant', zh: '添加餐厅' },
    delete: { en: 'Delete', zh: '删除' },
    deleteConfirm: { en: 'Are you sure you want to delete this restaurant?', zh: '确定要删除这家餐厅吗？' },
    previewDishes: { en: 'Preview Dishes', zh: '预览菜品' },
    dishPhotosTitle: { en: 'Top Dish Photos', zh: '招牌菜照片' },
    noDishPhotos: { en: 'No photos available for this restaurant', zh: '暂无该餐厅的照片' },
    loadingPhotos: { en: 'Loading photos...', zh: '加载照片中...' },

    // Travel time
    getTravelTimes: { en: 'Get Travel Times', zh: '获取路程时间' },
    loadingTravelTimes: { en: 'Loading...', zh: '加载中...' },
    startingAddress: { en: 'Starting address...', zh: '起始地址...' },
    noCoordinates: { en: 'No location', zh: '无位置信息' },

    // Maps notice
    mapsAutocompleteBlockedTitle: { en: 'Address recognition is unavailable', zh: '地址识别不可用' },
    mapsAutocompleteBlockedBody: { en: 'This feature resolves addresses on the server. If your network blocks Google Maps services or the server key is misconfigured, recognition may fail. You can still type manually.', zh: '该功能会在后端解析地址。如果网络无法访问 Google 服务或后端 Key 配置有问题，识别可能失败。你仍然可以手动输入。' },
    dismissNotice: { en: 'Dismiss', zh: '不再提示' },

    // Starting address recognition
    resolvingAddress: { en: 'Recognizing…', zh: '识别中…' },
    recognizedAddress: { en: 'Recognized', zh: '识别结果' },
    unrecognizedAddress: { en: 'No match', zh: '未匹配' },
    addressRecognizedSuccess: { en: '✓ Address recognized', zh: '✓ 已识别地址' },

    // Config panel
    configTitle: { en: 'Restaurant Filter', zh: '餐厅筛选' },
    configStartingLocation: { en: 'Starting location', zh: '起始位置' },
    configUseCurrentLocation: { en: 'Use current location', zh: '使用当前位置' },
    configOrTypeAddress: { en: 'Or type an address...', zh: '或输入地址...' },
    configTravelMethod: { en: 'Travel method', zh: '出行方式' },
    configMaxTravelTime: { en: 'Max travel time', zh: '最大路程时间' },
    configPreviewBtn: { en: 'Preview Restaurants', zh: '预览餐厅' },
    configEmptyMessage: { en: 'Configure and preview filter to see available restaurants', zh: '配置并预览筛选条件以查看可用餐厅' },
    configLoadingMessage: { en: 'Loading...', zh: '加载中...' },
    configNoResults: { en: 'No restaurants match the criteria', zh: '没有符合条件的餐厅' },
    configRestaurantCount: { en: 'restaurants found', zh: '家餐厅' },
    configTravelModeDrive: { en: 'Drive', zh: '开车' },
    configTravelModeTransit: { en: 'Transit', zh: '公共交通' },
    configTravelModeWalk: { en: 'Walk', zh: '步行' },
    configTravelModeBike: { en: 'Bike', zh: '骑行' },

    // Filter summary bar
    filterSummaryExpand: { en: 'Expand', zh: '展开' },
    filterSummaryNoLocation: { en: 'No location set', zh: '未设置位置' },
    filterSummaryMinFrom: { en: 'min or less from', zh: '分钟以内 从' },

    // Recommend button
    recommendBtnText: { en: 'Recommend', zh: '推荐' },
    recommendLoading: { en: 'Loading...', zh: '加载中...' },

    // Visit history panel
    visitHistoryTitle: { en: 'Dining History', zh: '用餐记录' },
    visitHistoryEmpty: { en: 'No dining history yet.', zh: '暂无用餐记录。' },
    visitHistoryBtn: { en: 'History', zh: '记录' },
    visitHistorySortLabel: { en: 'Sort:', zh: '排序：' },
    visitHistorySortNewest: { en: 'Newest First', zh: '最新优先' },
    visitHistorySortOldest: { en: 'Oldest First', zh: '最早优先' },
    visitHistorySortRestaurant: { en: 'By Restaurant', zh: '按餐厅' },
    visitHistoryDeleteConfirm: { en: 'Are you sure you want to delete this visit record?', zh: '确定要删除这条用餐记录吗？' },

    // Auth modal
    authModalTitle: { en: 'Access Token Required', zh: '需要访问令牌' },
    authModalDesc: { en: 'Please enter your access token to continue.', zh: '请输入访问令牌以继续使用。' },
    authModalSubmit: { en: 'Submit', zh: '提交' },
    authModalError: { en: 'Invalid token. Please try again.', zh: '无效的令牌，请重试。' },
    changeTokenBtn: { en: 'Change Token', zh: '更换令牌' },

    // Location status
    currentLocation: { en: 'Current Location', zh: '当前位置' },
    gettingLocation: { en: 'Getting location…', zh: '正在获取位置…' },
    currentLocationPrefix: { en: 'Current location:', zh: '当前位置：' },
    willUseTypedAddress: { en: 'Will use typed address', zh: '将使用输入的地址' },
    willUseCurrentLocation: { en: 'Will use current location', zh: '将使用当前位置' },
    pleaseSelectLocation: { en: 'Please select location', zh: '请选择位置' },
    locatingResolvingGeo: { en: 'Resolving coordinates...', zh: '正在解析坐标...' },
    locatingLoadingCoords: { en: 'Loading coordinates...', zh: '正在加载坐标...' },
    locatingFailed: { en: 'Failed to get location', zh: '无法获取位置' },
    locatingWaitingGeo: { en: 'Waiting for browser permission...', zh: '等待浏览器授权...' },
    geoNotSupported: { en: 'Geolocation not supported', zh: '不支持地理定位' },
    geoWatchStartFailed: { en: 'Failed to start location tracking', zh: '无法开始位置跟踪' },

    // Restaurant form
    editRestaurant: { en: 'Edit Restaurant', zh: '编辑餐厅' },
    save: { en: 'Save', zh: '保存' },
    googlePlaceId: { en: 'Google Place ID', zh: 'Google Place ID' },
    addressLabel: { en: 'address', zh: '地址' },
    duplicateWarning: { en: 'A restaurant with this address already exists', zh: '该地址的餐厅已存在' },
    updateRestaurant: { en: 'Update Restaurant', zh: '更新餐厅' },
    updateExisting: { en: 'Update Existing Restaurant', zh: '更新已有餐厅' },
    loadFailed: { en: 'Failed to load', zh: '加载失败' },
    formNameEnLabel: { en: 'English Name', zh: '英文名' },
    formNameEnPlaceholder: { en: 'e.g., Golden Dragon', zh: '例如：金龙饭店' },
    formNameZhLabel: { en: 'Chinese Name', zh: '中文名' },
    formNameZhPlaceholder: { en: 'e.g., 金龙 (optional)', zh: '例如：金龙（可选）' },
    formMagicFill: { en: 'Magic Fill', zh: '魔法填充' },
    formMagicFillTitle: { en: 'Octopus Magic Fill', zh: '章鱼魔法填充' },
    formCuisineLabel: { en: 'Cuisine Type', zh: '菜系' },
    formCuisinePlaceholder: { en: 'e.g., Chinese, Sichuan, Italian', zh: '例如：中餐，川菜，意大利菜' },
    formAddressLabel: { en: 'Address', zh: '地址' },
    formAddressPlaceholder: { en: 'e.g., 123 Main St, San Francisco, CA 94102', zh: '例如：成都市武侯区人民南路 1 号' },
    formPlaceIdLabel: { en: 'Google Place ID', zh: 'Google Place ID' },
    formPlaceIdPlaceholder: { en: '(Auto-filled from Magic Fill or enter manually)', zh: '（魔法填充自动填写或手动输入）' },
    formPriceLevelLabel: { en: 'Price Level', zh: '价格等级' },
    formBudgetCategoryLabel: { en: 'Budget Category', zh: '预算分类' },
    formOptionNotSet: { en: 'Not set', zh: '未设置' },
    formPriceBudget: { en: '$ - Budget', zh: '$ - 经济' },
    formPriceModerate: { en: '$$ - Moderate', zh: '$$ - 适中' },
    formPriceExpensive: { en: '$$$ - Expensive', zh: '$$$ - 偏贵' },
    formPriceVeryExpensive: { en: '$$$$ - Very Expensive', zh: '$$$$ - 很贵' },
    formBudgetQuickMeal: { en: 'Quick Meal', zh: '简餐' },
    formBudgetCasual: { en: 'Casual', zh: '休闲' },
    formBudgetModerate: { en: 'Moderate', zh: '中等' },
    formBudgetFineDining: { en: 'Fine Dining', zh: '精致餐饮' },
    formTopDishesLabel: { en: 'Top Dishes (comma-separated)', zh: '招牌菜（逗号分隔）' },
    formTopDishesPlaceholder: { en: 'e.g., Mapo Tofu, Kung Pao Chicken, Hot Pot', zh: '例如：麻婆豆腐、宫保鸡丁、火锅' },
    formKeywordsLabel: { en: 'Keywords (comma-separated)', zh: '关键词（逗号分隔）' },
    formKeywordsPlaceholder: { en: 'e.g., 川菜, 适合约会, 适合独食', zh: '例如：川菜、适合约会、适合独食' },
    formTagsLabel: { en: 'Tags', zh: '标签' },
    formTagsPlaceholder: { en: 'Type to add tags...', zh: '输入以添加标签...' },
    formOpeningHoursLabel: { en: 'Opening Hours', zh: '营业时间' },
    formOpeningHoursPlaceholder: {
        en: 'e.g., Mon-Fri: 11:00 AM - 10:00 PM, Sat-Sun: 10:00 AM - 11:00 PM',
        zh: '例如：周一至周五 11:00-22:00，周末 10:00-23:00'
    },
    formSummaryLabel: { en: 'Summary (for AI recommendations)', zh: '简介（用于 AI 推荐）' },
    formSummaryPlaceholder: {
        en: 'Describe the restaurant - specialties, ambiance, price range, best dishes, etc.',
        zh: '描述餐厅特色、环境、价位、招牌菜等。'
    },

    // Restaurant card
    edit: { en: 'Edit', zh: '编辑' },
    visitHistoryLabel: { en: 'Visit History', zh: '访问记录' },
    hideDishes: { en: 'Hide Dishes', zh: '隐藏菜品' },
    wantToGo: { en: 'Want to Go', zh: '想去' },
    wantToGoMarked: { en: 'Marked as want to go', zh: '已标记为想去' },
    wantToGoUnmarked: { en: 'Unmarked want to go', zh: '已取消想去标记' },

    // Visit records
    editVisit: { en: 'Edit', zh: '编辑' },
    deleteVisit: { en: 'Delete', zh: '删除' },
    editVisitTitle: { en: 'Edit Visit Record', zh: '编辑访问记录' },
    dateLabel: { en: 'Date', zh: '日期' },
    noteLabel: { en: 'Note', zh: '记录' },
    addVisit: { en: 'Add Visit', zh: '添加记录' },
    addVisitTitle: { en: 'Add Visit Record', zh: '添加访问记录' },
    addVisitPlaceholder: { en: 'What did you have? How was it?', zh: '吃了什么？感觉如何？' },
    visitRecordCreated: { en: 'Visit record created', zh: '访问记录已创建' },
    visitRecordUpdated: { en: 'Visit record updated (same date)', zh: '访问记录已更新（相同日期）' },

    // Chat
    image: { en: 'Image', zh: '图片' },
    audio: { en: 'Audio', zh: '音频' },
    copyMessage: { en: 'Copy', zh: '复制' },
    copiedMessage: { en: 'Copied!', zh: '已复制！' },
    thinking: { en: 'Thinking', zh: '思考中' },
    modelThinking: { en: 'Thinking...', zh: '思考中...' },
    modelThought: { en: 'Thought', zh: '思考过程' },
    whatIsThis: { en: 'What is this?', zh: '这是什么？' },
    expand: { en: 'Expand', zh: '展开' },
    collapse: { en: 'Collapse', zh: '收起' },
    stopResponse: { en: 'Stop response', zh: '停止回复' },
    responseStopped: { en: 'Response stopped', zh: '回复已停止' },

    // Filter labels
    filterApplied: { en: 'Filter applied', zh: '已应用筛选' },
    allRestaurants: { en: 'All restaurants', zh: '全部餐厅' },
    noRestaurantsByFilter: { en: 'No restaurants within', zh: '分钟内无餐厅' },

    // Search
    noMatchingRestaurantsSearch: { en: 'No matching restaurants', zh: '没有匹配的餐厅' },

    // Fast fill
    searching: { en: 'Searching...', zh: '搜索中...' },
    recognized: { en: 'Recognized', zh: '识别到' },
    noMatchingRestaurants: { en: 'No matching restaurants found', zh: '未找到匹配的餐厅' },
    selected: { en: 'Selected', zh: '已选择' },
    pleaseEnterName: { en: 'Please enter a restaurant name first', zh: '请先输入餐厅名称' },
    noRestaurantsFound: { en: 'No restaurants found', zh: '未找到餐厅' },
    fetchingDetails: { en: 'Fetching details...', zh: '获取信息中...' },
    autoFillSuccess: { en: 'Auto-filled restaurant info!', zh: '自动填充成功！' },
    autoFillFailed: { en: 'Failed to auto-fill', zh: '自动填充失败' },
    selectRestaurant: { en: 'Please select a restaurant', zh: '请选择餐厅' },
    confirm: { en: 'Confirm', zh: '确认' },

    // Errors
    networkError: { en: 'Network error', zh: '网络错误' },
    recordingError: { en: 'Recording error, please try again', zh: '录音出错，请重试' },
    microphoneAccessError: { en: 'Cannot access microphone. Please check browser permissions.', zh: '无法访问麦克风。请检查浏览器权限设置。' },
    recordingTooLong: { en: 'Recording too long. Please record a shorter audio (max 10MB)', zh: '录音太长，请录制较短的音频（最大10MB）' },
    audioProcessError: { en: 'Could not process audio data', zh: '无法处理音频数据' },
    audioReadError: { en: 'Could not read audio data', zh: '无法读取音频数据' },

    // Geolocation errors
    geoPermissionDenied: { en: 'Location permission denied. Please allow in browser settings.', zh: '位置权限被拒绝，请在浏览器设置中允许' },
    geoPositionUnavailable: { en: 'Location information unavailable.', zh: '位置信息不可用' },
    geoTimeout: { en: 'Location request timed out.', zh: '获取位置超时' },
    geoUnknownError: { en: 'Unknown error.', zh: '未知错误' },

    // Image upload errors
    imageSelectFile: { en: 'Please select an image file', zh: '请选择图片文件' },
    imageTooLarge: { en: 'Image too large. Please select an image under 20MB', zh: '图片太大，请选择小于20MB的图片' },
    imageProcessError: { en: 'Could not process image', zh: '无法处理图片' },
    imageReadError: { en: 'Could not read image data', zh: '无法读取图片数据' },

    // System prompts
    systemPromptBase: { en: '🤖 System Prompt (Base Instructions + Known Restaurants)', zh: '🤖 系统提示 (基础指令 + 已知餐厅)' },
    systemPromptFiltered: { en: '📍 Filtered Candidates (With Filter Criteria)', zh: '📍 过滤后的候选餐厅 (含筛选条件)' },
    systemPromptChip: { en: '🤖 System Prompt', zh: '🤖 系统提示' },
    systemPromptSectionBase: { en: 'Base Instructions', zh: '基础指令' },
    systemPromptSectionRestaurants: { en: 'Known Restaurants', zh: '已知餐厅' },
    systemPromptSectionFiltered: { en: 'Filtered Candidates', zh: '筛选后的候选' },
    toolCallSuccess: { en: 'Tool Call Success', zh: '工具调用成功' },
    toolCallFailed: { en: 'Tool Call Failed', zh: '工具调用失败' },
    toolCallLabel: { en: 'Tool call', zh: '工具调用' },
    toolResultLabel: { en: 'Tool result', zh: '工具结果' },
    imageErrorSuffix: { en: 'Please try using a different image format (JPEG, PNG, WebP, or GIF).', zh: '请尝试使用其他图片格式（JPEG、PNG、WebP 或 GIF）。' },
    pleaseTryAgain: { en: 'Please try again later.', zh: '请稍后重试。' },
    noResponseGenerated: { en: 'No response generated. The model may be experiencing issues.', zh: '未生成回复。模型可能遇到问题。' },
    modelCouldntComplete: { en: "Model couldn't complete response. Please try again.", zh: '模型未能完成回复，请重试。' },
    stopReasonToolCallLimit: { en: 'Reached tool call limit. Summarizing with current results.', zh: '已达到工具调用限制，正在用当前结果总结。' },

    // Progress indicators (streaming)
    progressLoadingRestaurants: { en: 'Loading restaurants...', zh: '正在加载餐厅数据...' },
    progressBuildingContext: { en: 'Preparing recommendations...', zh: '正在准备推荐...' },
    progressCallingAI: { en: 'Generating response...', zh: '正在生成回复...' },
    progressDefault: { en: 'Processing...', zh: '处理中...' },

    // Magic Import
    magicImportBtn: { en: 'Magic Import', zh: '魔法导入' },
    magicImportTitle: { en: 'Magic Import', zh: '魔法导入' },
    magicImportTagLabel: { en: 'Tags (optional)', zh: '标签（可选）' },
    magicImportTagPlaceholder: { en: 'e.g., Brunch, Weekend, Favorites', zh: '例如：早午餐，周末，最爱' },
    magicImportLocationLabel: { en: 'Preferred Location (optional)', zh: '首选位置（可选）' },
    magicImportLocationPlaceholder: { en: 'e.g., California, US', zh: '例如：California, US' },
    magicImportTextLabel: { en: 'Paste Text', zh: '粘贴文本' },
    magicImportTextPlaceholder: { en: 'Paste restaurant names here (one per line or comma-separated)', zh: '在此粘贴餐厅名称（每行一个或逗号分隔）' },
    magicImportFileLabel: { en: 'Or Upload File', zh: '或上传文件' },
    magicImportExtract: { en: 'Extract Names', zh: '提取名称' },
    magicImportPreviewTitle: { en: 'Preview Restaurant Names', zh: '预览餐厅名称' },
    magicImportStartImport: { en: 'Start Import', zh: '开始导入' },
    magicImportProcessing: { en: 'Importing', zh: '导入中' },
    magicImportComplete: { en: 'Import Complete', zh: '导入完成' },
    magicImportCreated: { en: 'created', zh: '新建' },
    magicImportDuplicate: { en: 'duplicate', zh: '重复' },
    magicImportNotFound: { en: 'not found', zh: '未找到' },
    magicImportError: { en: 'error', zh: '错误' },
    magicImportDone: { en: 'Done', zh: '完成' },
    magicImportHelpTitle: { en: 'Import Help', zh: '导入帮助' },
    magicImportHelpText: {
        en: 'Paste restaurant names directly or upload a file. The system will intelligently extract restaurant names from any format.',
        zh: '直接粘贴餐厅名称或上传文件。系统会智能提取任何格式中的餐厅名称。'
    },
    magicImportTakeoutTip: {
        en: 'Tip: Export your saved places from Google Takeout',
        zh: '提示：从 Google Takeout 导出您保存的地点'
    },
    magicImportTakeoutLink: {
        en: 'Go to takeout.google.com, select "Saved" under "Maps", and download.',
        zh: '前往 takeout.google.com，在"地图"下选择"已保存"，然后下载。'
    },
    magicImportNoInput: { en: 'Please paste text or select a file', zh: '请粘贴文本或选择文件' },
    magicImportStatusLegend: { en: 'Status Icons:', zh: '状态图标：' },
    magicImportStatusPending: { en: 'Waiting', zh: '等待中' },
    magicImportStatusProcessing: { en: 'Searching on Google Maps', zh: '正在 Google Maps 上搜索' },
    magicImportStatusSuccess: { en: 'Added to database', zh: '已添加到数据库' },
    magicImportStatusDuplicate: { en: 'Already in database', zh: '已在数据库中' },
    magicImportStatusNotFound: { en: 'Not found on Google Maps', zh: '在 Google Maps 上未找到' },
    magicImportStatusError: { en: 'Error occurred', zh: '发生错误' },
    magicImportResultCreatedDesc: { en: 'New restaurants added to your database', zh: '新添加到数据库的餐厅' },
    magicImportResultDuplicateDesc: { en: 'Already existed in your database (skipped)', zh: '已存在于数据库中（已跳过）' },
    magicImportResultNotFoundDesc: { en: 'Could not find on Google Maps - check spelling or try different name', zh: '在 Google Maps 上找不到 - 请检查拼写或尝试其他名称' },
    magicImportResultErrorDesc: { en: 'Failed due to network or server error', zh: '由于网络或服务器错误而失败' },

    // Injected context messages (new architecture)
    injectedContextLabel: { en: 'Restaurant Database', zh: '餐厅数据库' },
    injectedVisitsLabel: { en: 'Recent Visits', zh: '最近用餐' },
    injectedFilterLabel: { en: 'Filter Applied', zh: '已应用筛选' },
    injectedFilterUpdateLabel: { en: 'Filter Updated', zh: '筛选已更新' },

    // Restaurant list sorting
    restaurantSortLabel: { en: 'Sort:', zh: '排序：' },
    restaurantSortByName: { en: 'Name', zh: '名称' },
    restaurantSortByAdded: { en: 'Date Added', zh: '添加日期' },
    restaurantSortByLastVisit: { en: 'Last Visit', zh: '最近访问' },

    // Config panel hints
    configLocationRequiredHint: {
        en: 'A starting location is required. Your browser denied location access or geolocation is unavailable. Please enter an address manually.',
        zh: '需要设置起始位置。浏览器拒绝了位置访问权限或地理定位不可用。请手动输入地址。'
    },
} as const;

// Derive the UIStrings type from the STRINGS object
type StringKey = keyof typeof STRINGS;
export type UIStrings = { [K in StringKey]: string };

/** Build a language-specific strings object from STRINGS */
function buildStrings(lang: Language): UIStrings {
    const result = {} as UIStrings;
    for (const key of Object.keys(STRINGS) as StringKey[]) {
        result[key] = STRINGS[key][lang];
    }
    return result;
}

/** Current UI strings - rebuilt when language changes */
export let i18n: UIStrings = buildStrings('en');

/** Current language */
let currentLanguage: Language = 'en';

/** Get the current language */
export function getLanguage(): Language {
    return currentLanguage;
}

/** Set the current language and update i18n strings */
export function setLanguage(lang: Language): void {
    currentLanguage = lang;
    i18n = buildStrings(lang);
}

function getString(key: string): string | null {
    const value = (i18n as Record<string, string>)[key];
    return value ?? null;
}

export function applyTranslations(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((element) => {
        const key = element.dataset.i18n;
        if (!key) return;
        const value = getString(key);
        if (value !== null) element.textContent = value;
    });

    root.querySelectorAll<HTMLElement>('[data-i18n-attr]').forEach((element) => {
        const spec = element.dataset.i18nAttr;
        if (!spec) return;
        const pairs = spec
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean);

        for (const pair of pairs) {
            const [attr, key] = pair.split(':').map((entry) => entry.trim());
            if (!attr || !key) continue;
            const value = getString(key);
            if (value !== null) element.setAttribute(attr, value);
        }
    });
}
